import axios, { AxiosError } from "axios";
import { useDeviceStore } from "../lib/store/useDeviceStore";
import { refreshToken } from "./v1/auth.service";
import { useAccessStore } from "../lib/store/useAccessStore";
import { useModalStore } from "../lib/store/useModalStore";
import type { AccessProps } from "../types/auth.types";
import { tokenRefreshQueue } from "./queue";
import type { ErrorResponse, Response } from "./dto/response/common.response";
import { resetUserSession } from "./queryClient";

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

export const setGlobalAuthHeader = (token: string) => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

apiClient.interceptors.request.use(
  (config) => {
    const deviceType = useDeviceStore.getState().deviceType;
    let token = "";

    try {
      const access_items = localStorage.getItem("parkgolf-access");
      if (access_items) {
        const access = JSON.parse(access_items);
        token = access.state?.access?.accessToken;
      }
    } catch (error) {
      console.error("Failed to parse auth token form storage:", error);
    }

    if (config.headers) {
      config.headers["Device-Type"] = deviceType;
      if (token && !config.url?.includes("/v1/auth/refresh-token")) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/** 타임아웃·오프라인 등 HTTP 응답이 없는 오류. 서버가 리프레시를 거절한 것이 아니다. */
export const isNetworkOrTimeoutError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  return !error.response;
};

const TIMEOUT_ALERT_TITLE = "통신장애";

export const isTimeoutError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  if (error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ETIMEDOUT) {
    return true;
  }
  return /timeout/i.test(error.message ?? "");
};

const SERVER_ERROR_CODE = "C003";

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

const isErrorResponse = (value: unknown): value is ErrorResponse =>
  typeof value === "object" &&
  value !== null &&
  "code" in value &&
  "message" in value &&
  isNullableString(value.code) &&
  isNullableString(value.message);

/** 서버가 내려준 오류 본문. 무응답이거나 형식이 다르면 undefined */
export const apiErrorOf = (error: unknown): ErrorResponse | undefined => {
  if (!axios.isAxiosError(error)) return undefined;
  const data: unknown = error.response?.data;
  if (typeof data !== "object" || data === null || !("error" in data)) {
    return undefined;
  }
  return isErrorResponse(data.error) ? data.error : undefined;
};

/** 서버 거절 문구. 서버 오류·문구 없음이면 기본 안내 */
export const apiErrorMessage = (body?: ErrorResponse | null): string =>
  body?.code !== SERVER_ERROR_CODE && body?.message
    ? body.message
    : "잠시 후 다시 시도해 주세요.";

export const serverErrorMessage = (error: unknown): string =>
  apiErrorMessage(apiErrorOf(error));

/** 요청 실패 안내. 타임아웃은 응답 인터셉터가 안내한다 */
export const alertRequestFailure = (title: string, error: unknown) => {
  if (isTimeoutError(error)) return;
  useModalStore
    .getState()
    .openModal("ALERT_DIALOG", { title, content: serverErrorMessage(error) });
};

const showTimeoutAlert = () => {
  const { modals, openModal } = useModalStore.getState();
  const alreadyOpen = modals.some(
    (m) =>
      m.key === "ALERT_DIALOG" &&
      (m.props as { title?: string } | undefined)?.title === TIMEOUT_ALERT_TITLE,
  );
  if (alreadyOpen) return;

  openModal("ALERT_DIALOG", {
    title: TIMEOUT_ALERT_TITLE,
    content: "일시적인 통신 장애가 발생했습니다.\n잠시 후 다시 시도해 주세요.",
  });
};

/* 토큰 갱신이 확정 실패했을 때 저장된 토큰을 비운다.
 * 요청 인터셉터가 localStorage 를 읽어 Authorization 을 붙이므로,
 * 스토어를 비워야 이후 요청에 죽은 토큰이 실리지 않는다.
 * 비우지 않으면 공개 API 까지 401 로 막혀 앱을 지우기 전엔 복구되지 않는다.
 *
 * 그 사이 다른 갱신 경로가 새 토큰을 확보했다면 로그아웃하지 않는다.
 * (리프레시 토큰 회전 경쟁 대비 — ProtectedRoute 와 같은 판단)
 */
const clearAuthOnRefreshFailure = () => {
  const store = useAccessStore.getState();
  if (store.isAuthenticated()) return;

  store.clearAccess();
  resetUserSession();
  delete apiClient.defaults.headers.common["Authorization"];
};

// 동일 요청 무한루프 방지 플래그
const RETRIED_FLAG = "x-retried";

declare module "axios" {
  export interface AxiosRequestConfig {
    [RETRIED_FLAG]?: boolean;
  }
}

apiClient.interceptors.response.use(
  (response) => {
    // 성공한 응답은 그대로 반환
    return response;
  },
  async (error: AxiosError) => {
    if (isTimeoutError(error)) {
      showTimeoutAlert();
    }

    const originalRequestConfig = error.config;

    // 1. 401 오류가 아니거나, config가 없으면 즉시 실패 처리
    if (!originalRequestConfig || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    // 2. 토큰 만료(C004) 코드가 아니면 즉시 실패 처리
    const errorCode = (error.response?.data as Response)?.error?.code;
    if (errorCode !== "C004") {
      return Promise.reject(error);
    }

    // 3. 리프레시 토큰 요청 자체가 401로 실패한 경우
    if (originalRequestConfig.url?.includes("/v1/auth/refresh-token")) {
      clearAuthOnRefreshFailure();
      return Promise.reject(error);
    }

    // 4. 이미 재시도한 요청인 경우
    if (originalRequestConfig[RETRIED_FLAG]) {
      return Promise.reject(error);
    }

    originalRequestConfig[RETRIED_FLAG] = true;

    // --- 5. 큐(Queue) 처리 (💡 클래스 메서드 사용) ---
    if (tokenRefreshQueue.getIsRefreshing()) {
      return new Promise((resolve, reject) => {
        // 💡 큐에 (resolve, reject) 함수를 등록
        tokenRefreshQueue.enqueueRequest(
          (token) => {
            // 토큰 갱신 성공 시, 새 토큰으로 헤더 교체
            originalRequestConfig.headers.Authorization = `Bearer ${token}`;
            // 큐에 있던 요청을 '재실행'
            resolve(apiClient(originalRequestConfig));
          },
          (err) => {
            // 토큰 갱신 실패 시, 큐에 있던 요청도 실패 처리
            reject(err);
          },
        );
      });
    }

    // --- 6. 최초의 401 요청 (토큰 갱신 실행) ---
    tokenRefreshQueue.setIsRefreshing(true); // 💡 갱신 상태로 변경

    try {
      // 갱신에 필요한 리프레시 토큰 가져오기
      const refresh_token = useAccessStore.getState().access?.refreshToken;
      if (!refresh_token) {
        throw new Error("No refresh token available.");
      }

      // 토큰 갱신 API 호출
      const { data } = await refreshToken(refresh_token);

      // 스토어에 새 토큰 저장
      useAccessStore.getState().setAccess(data as AccessProps);

      // 💡 갱신 성공: 큐에 쌓인 요청들 처리 (에러는 null, 토큰 전달)
      tokenRefreshQueue.processQueue(null, data.accessToken);

      // 현재 실패했던 원본 요청(originalRequestConfig) 재실행
      originalRequestConfig.headers.Authorization = `Bearer ${data.accessToken}`;
      return apiClient(originalRequestConfig);
    } catch (refreshErr) {
      // 💡 갱신 실패: 큐에 쌓인 요청들 처리 (에러 전달)
      tokenRefreshQueue.processQueue(refreshErr, null);

      // 네트워크/타임아웃은 세션을 유지한다. 서버가 리프레시를 거절한 경우에만 로그아웃.
      if (!isNetworkOrTimeoutError(refreshErr)) {
        clearAuthOnRefreshFailure();
      }
      return Promise.reject(refreshErr);
    } finally {
      tokenRefreshQueue.setIsRefreshing(false); // 💡 갱신 상태 해제
    }
  },
);
